import React, { useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, ArrowRightLeft, Info } from "lucide-react";

export const STPCalculatorPage = () => {
  const [sourceAmount, setSourceAmount] = useState(1000000);
  const [transferAmount, setTransferAmount] = useState(50000);
  const [months, setMonths] = useState(12);
  const [sourceReturn, setSourceReturn] = useState(6);
  const [targetReturn, setTargetReturn] = useState(12);
  const [result, setResult] = useState(null);

  const calculateSTP = () => {
    const P = sourceAmount;
    const T = transferAmount;
    const n = months;
    const rs = sourceReturn / 100 / 12;
    const rt = targetReturn / 100 / 12;
    
    let sourceBalance = P;
    let targetBalance = 0;
    let totalTransferred = 0;
    
    for (let i = 0; i < n; i++) {
      // Source fund grows and then transfer happens
      sourceBalance = sourceBalance * (1 + rs);
      const transferThisMonth = Math.min(T, sourceBalance);
      sourceBalance -= transferThisMonth;
      totalTransferred += transferThisMonth;
      
      // Target fund grows with new transfer
      targetBalance = (targetBalance + transferThisMonth) * (1 + rt);
    }
    
    // Continue growth for remaining months if any source balance
    const remainingMonths = 12 - n;
    if (remainingMonths > 0 && sourceBalance > 0) {
      sourceBalance = sourceBalance * Math.pow(1 + rs, remainingMonths);
    }
    
    setResult({
      initialAmount: P,
      totalTransferred: Math.round(totalTransferred),
      sourceRemaining: Math.round(sourceBalance),
      targetValue: Math.round(targetBalance),
      totalValue: Math.round(sourceBalance + targetBalance),
      gain: Math.round(sourceBalance + targetBalance - P)
    });
  };

  const formatCurrency = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              STP <span className="gold-text">Calculator</span>
            </h1>
            <p className="text-muted-foreground">
              Plan systematic transfers from debt to equity funds
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-primary" />
                  Transfer Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label>Initial Amount (Source Fund)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      value={sourceAmount}
                      onChange={(e) => setSourceAmount(Number(e.target.value))}
                      className="pl-8"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Monthly Transfer Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      value={transferAmount}
                      onChange={(e) => setTransferAmount(Number(e.target.value))}
                      className="pl-8"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Transfer Period (Months)</Label>
                  <Input
                    type="number"
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    min="1"
                    max="60"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Source Return (% p.a.)</Label>
                    <Input
                      type="number"
                      value={sourceReturn}
                      onChange={(e) => setSourceReturn(Number(e.target.value))}
                      step="0.5"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Target Return (% p.a.)</Label>
                    <Input
                      type="number"
                      value={targetReturn}
                      onChange={(e) => setTargetReturn(Number(e.target.value))}
                      step="0.5"
                    />
                  </div>
                </div>

                <Button onClick={calculateSTP} className="w-full">
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate STP
                </Button>
              </CardContent>
            </Card>

            <div className="space-y-6">
              {result && (
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle>STP Analysis</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 rounded-lg bg-blue-500/10">
                        <p className="text-xs text-muted-foreground">Source Remaining</p>
                        <p className="text-lg font-bold text-blue-500">{formatCurrency(result.sourceRemaining)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-green-500/10">
                        <p className="text-xs text-muted-foreground">Target Value</p>
                        <p className="text-lg font-bold text-green-500">{formatCurrency(result.targetValue)}</p>
                      </div>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
                      <p className="text-sm text-muted-foreground">Total Portfolio Value</p>
                      <p className="text-2xl font-bold gold-text">{formatCurrency(result.totalValue)}</p>
                      <p className="text-sm text-green-500 mt-1">
                        Gain: {formatCurrency(result.gain)}
                      </p>
                    </div>

                    <div className="text-sm text-muted-foreground">
                      <p>Total Transferred: {formatCurrency(result.totalTransferred)}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div className="text-sm text-muted-foreground space-y-2">
                      <p><strong>STP:</strong> Transfer money systematically from one fund to another.</p>
                      <p><strong>Common Use:</strong> Move from liquid/debt fund to equity fund to average out entry price.</p>
                      <p><strong>Benefit:</strong> Reduces timing risk when investing large amounts.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
